import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { pageVariants, pageTransition } from '../../constants/GlobalStyles';
import UserProfilePageStyle from './UserProfilePage.module';
import AuthContext from '../../contexts/auth-context';
import AppContext from '../../contexts/app-context';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import useAxios from '../../hooks/useAxios';
import { app_paths } from '../../components/config/variables';
import Button from '@mui/material/Button';

const DoctorProfilePage = () => {
  const classes = UserProfilePageStyle();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const { operation } = useAxios();
  const [doctorProfile, setDoctorProfile] = useState([]);
  const [loading, setLoadingUsersList] = useState(false);
  const [error, setDoctorProfileError] = useState();
  const doctor = appCtx.userRole === 'facility_admin' ? doctorProfile.user : authCtx.userProfile.user;
  const direction = appCtx.direction;
  const { doctorID } = useParams();
  const { t } = useTranslation('doctorProfilePage');

  const handleSetDoctorProfile = (res) => {
    setLoadingUsersList(false);
    setDoctorProfile(res);
  };

  const getDoctorProfileById = () => {
    setLoadingUsersList(true);
    operation(
      {
        method: 'GET',
        url: `/api/user/doctorprofile/get/${doctorID}`,
        headers: { Authorization: `token ${authCtx.token}` },
        data: {},
      },
      handleSetDoctorProfile,
      setDoctorProfileError
    );
  };

  useEffect(() => {
    if (doctorID && appCtx.userRole === 'facility_admin') getDoctorProfileById();
    // eslint-disable-next-line
  }, [doctorID]);

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box dir={direction}>
          <Alert severity="error">
            <AlertTitle>{t('Error')}</AlertTitle>
            {t('FailedToLoadDoctorProfile')}
          </Alert>
        </Box>
      ) : authCtx.error ? (
        <Box dir={direction}>
          <Alert severity="error">
            <AlertTitle sx={classes.alertTitle}>{t('NoDoctorInfoTitle')}</AlertTitle>
            {t('NoDoctorInfoMessage')}
          </Alert>
        </Box>
      ) : (
        <Box>
          <IconButton size="small" sx={classes.arrow_button} onClick={() => navigate(-1)}>
            <ArrowBackIcon fontSize="small" sx={classes.arrow_icon} />
          </IconButton>

          <Box sx={classes.container}>
            <Box sx={classes.user_info_container} dir={direction}>
              <Box sx={classes.avatar_container}>
                <Avatar
                  alt="doctor"
                  src={doctor?.gender === 'F' ? '/images/doctor_woman.svg' : '/images/doctor_man.svg'}
                  sx={classes.user_avatar}
                />
                <Typography sx={classes.user_name}>{doctor?.name}</Typography>
                      { doctorID && appCtx.userRole === 'facility_admin' && <NavLink to={app_paths.UPDATE_DOCTOR_PROFILE_FOR_FACILITY.replace(':doctorID', doctorID)} style={classes.new_encounter_link}>
                        <Button sx={[
                          classes.patient_profile_button,
                          direction === 'ltr' ? { marginRight: '20px' } : { marginLeft: '20px' },
                        ]}>
                          Update Doctor
                        </Button>
                      </NavLink>}
              </Box>

              <Box sx={classes.user_details}>
                <Box sx={classes.user_info_box}>
                  <Typography>{t('GovernmentID')}</Typography>
                  <Typography component="span" sx={classes.user_info_value}>{doctor?.eid}</Typography>
                </Box>

                <Box sx={classes.user_info_box}>
                  <Typography>{t('Nationality')}</Typography>
                  <Typography component="span" sx={classes.user_info_value}>{doctor?.nationality}</Typography>
                </Box>

                <Box sx={classes.user_info_box}>
                  <Typography>{t('DateOfBirth')}</Typography>
                  <Typography component="span" sx={classes.user_info_value}>{doctor?.birthdate}</Typography>
                </Box>

                <Box sx={classes.user_info_box}>
                  <Typography>{t('Age')}</Typography>
                  <Typography component="span" sx={classes.user_info_value}>{doctor?.age} Y</Typography>
                </Box>

                <Box sx={classes.user_info_box}>
                  <Typography>{t('Gender')}</Typography>
                  <Typography component="span" sx={classes.user_info_value}>
                    {doctor?.gender === 'F' ? 'Female' : 'Male'}
                  </Typography>
                </Box>

                <Box sx={classes.user_info_box}>
                  <Typography>{t('Employer')}</Typography>
                  <Typography component="span" sx={classes.user_info_value}>{doctor?.employer}</Typography>
                </Box>

                      {appCtx.userRole === 'facility_admin' && (
                        <>
                          <Box sx={classes.user_info_box}>
                            <Typography>License</Typography>
                            <Typography component="span" sx={classes.user_info_value}>{doctorProfile?.license}</Typography>
                          </Box>

                          <Box sx={classes.user_info_box}>
                            <Typography>Department</Typography>
                            <Typography component="span" sx={classes.user_info_value}>{doctorProfile?.department}</Typography>
                          </Box>

                          <Box sx={classes.user_info_box}>
                            <Typography>division</Typography>
                            <Typography component="span" sx={classes.user_info_value}>{doctorProfile?.division}</Typography>
                          </Box>
                        </>
                      )}

                      <Box sx={classes.user_info_box}>
                  <Typography>{t('MobileNumber')}</Typography>
                  <Typography component="span" sx={classes.user_info_value}>{doctor?.phone_number}</Typography>
                </Box>

                <Box sx={classes.user_info_box_extended}>
                  <Typography>{t('EmailAddress')}</Typography>
                  <Typography component="span" sx={classes.user_info_value}>{doctor?.email}</Typography>
                </Box>

                <Box sx={classes.user_info_box_extended}>
                  <Typography>{t('PhysicalAddress')}</Typography>
                  <Typography component="span" sx={classes.user_info_value}>{doctor?.physical_address}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </motion.div>
  );
};

export default DoctorProfilePage;
